// domain/services/chargeMasterSeries.js

/**
 * Which series a clinic's charge-master documents belong to, before any date is resolved.
 *
 * WHY THIS EXISTS AT ALL. The clinic's sheets are read by clinic, not by `chargeMasterId`, because
 * today there is exactly one series per clinic and the clinic identifies it. That is a bet with a
 * known expiry: when a clinic bills under more than one Type-2 NPI it will have more than one series,
 * and a read by clinic would return them interleaved. `VersionTimeline` would then resolve them as
 * though they were one series and pick sheets by date alone, silently charging from the wrong one.
 *
 * SO THE AMBIGUITY IS DETECTED RATHER THAN ASSUMED AWAY. Three outcomes, told apart deliberately:
 * no series yet (the clinic has never saved a charge master — create one), exactly one (the ordinary
 * case), and more than one (refuse, hold, and let a human sort it out). Collapsing the first and
 * third into a single null would make "nothing here yet" and "I cannot tell which of these applies"
 * the same answer, and the first invites a create where the third must never get one.
 *
 * SEPARATE FROM `chargeMasterResolution`, which takes a sheet that has already been resolved for a
 * date and asks what it charges. This runs one step earlier and answers a different question. Keeping
 * them apart is what stops a second, subtly different resolver growing inside the first.
 */

/**
 * The currency this series states its charges in, or null if none of its sheets says.
 *
 * A PROPERTY OF THE SERIES, carried on every sheet because this pattern has no separate document for
 * the series itself — the same arrangement as a funder's name, which every version repeats. It is
 * NOT a property of a line: every line in a sheet is necessarily in one currency, so a per-line
 * value would make two lines in one sheet able to disagree, and no billing entity could produce
 * that state.
 *
 * A CLINIC BILLING IN TWO CURRENCIES HAS TWO CHARGE MASTERS, not one with mixed lines. Currency
 * follows the billing entity — the thing holding the Type-2 NPI — so a clinic operating across
 * countries under separate entities has one series per entity, which is what `chargeMasterId` exists
 * to make expressible.
 *
 * @param {import('../model/ChargeMaster.types').ChargeMaster[]} versions
 * @returns {string|null}
 */
export function currencyOf(versions) {
  return (versions ?? [])
    .map((version) => version?.currency)
    .find((currency) => typeof currency === 'string' && currency.length > 0) ?? null;
} // currencyOf()

/**
 * The one series in a clinic's sheets, or an honest statement that there is not one.
 *
 * @param {import('../model/ChargeMaster.types').ChargeMaster[]} versions - every sheet read for the
 *   clinic, tombstones included; numbering has to count them
 * @returns {{ chargeMasterId: string|null, versions: import('../model/ChargeMaster.types').ChargeMaster[],
 *   isAmbiguous: boolean }} `chargeMasterId` is null when there is no series yet AND when there is
 *   more than one — `isAmbiguous` is what tells those apart, and a caller must check it before
 *   treating a null as "create one".
 */
export function soleSeriesOf(versions) {
  const seriesIds = [...new Set(
    (versions ?? [])
      .map((version) => version?.chargeMasterId)
      .filter((chargeMasterId) => typeof chargeMasterId === 'string' && chargeMasterId.length > 0),
  )];

  if (seriesIds.length === 0) {
    return { chargeMasterId: null, versions: [], isAmbiguous: false };
  }
  if (seriesIds.length > 1) {
    return { chargeMasterId: null, versions: [], isAmbiguous: true };
  }
  const [chargeMasterId] = seriesIds;

  return {
    chargeMasterId,
    versions: (versions ?? []).filter((version) => version?.chargeMasterId === chargeMasterId),
    isAmbiguous: false,
  };
} // soleSeriesOf()
